import { z } from 'zod';
import { ActorType, DecisionValue, Id, Timestamp } from './schemas/common.js';
import { WeeklyDigestRef } from './schemas/metrics.js';
import { SupportEndReason, SupportScope } from './schemas/platform.js';

/**
 * Catalogue des événements métier. Émis par `events.emit(tx, ctx, event)` dans la même transaction
 * que la modification (outbox). L4 (notifications) est le seul consommateur en V0.
 * Ajouter un événement = modification de contrat (L0, via docs/CHANGE-REQUESTS.md).
 */
export const EVENT_PAYLOADS = {
  'intervention.received': z.strictObject({ interventionId: Id, customerId: Id }),
  'media.published': z.strictObject({ interventionId: Id, mediaIds: z.array(Id).min(1) }),
  'complement.sent': z.strictObject({ complementId: Id, version: z.int().positive() }),
  'complement.decided': z.strictObject({
    complementId: Id,
    version: z.int().positive(),
    decision: DecisionValue,
    decidedAt: Timestamp,
  }),
  'question.asked': z.strictObject({ interventionId: Id, messageId: Id }),
  'question.answered': z.strictObject({ interventionId: Id, messageId: Id }),
  'vehicle.ready': z.strictObject({ interventionId: Id, readyAt: Timestamp, freeUntil: Timestamp }),
  'intervention.closed': z.strictObject({ interventionId: Id }),
  'intervention.reopened': z.strictObject({ interventionId: Id, reopenedAt: Timestamp }),
  // Vague 5 : accès support avec consentement. Le motif et les noms se relisent dans `support_access`
  // (garage de l'enveloppe, sous RLS) : la charge utile ne porte que des identifiants et des dates.
  'support.requested': z.strictObject({ supportAccessId: Id, requestedDurationMinutes: z.int().positive() }),
  'support.granted': z.strictObject({
    supportAccessId: Id,
    grantedUntil: Timestamp,
    scope: SupportScope,
    amountsVisible: z.boolean(),
  }),
  'support.ended': z.strictObject({ supportAccessId: Id, endReason: SupportEndReason, endedAt: Timestamp }),
  // Vague 6 (ajout 10) : fiche de réception partagée avec le client (e-mail sans pièce jointe, L4).
  'entry_report.shared': z.strictObject({ interventionId: Id, entryReportId: Id, version: z.int().positive() }),
  // Vague 6 (ajout 11) : rendez-vous (e-mails facultatifs, réglage `notifyPlannedVisit`, L4). Aucun lien portail.
  'visit.planned': z.strictObject({ plannedInterventionId: Id, plannedFor: z.iso.date() }),
  'visit.rescheduled': z.strictObject({ plannedInterventionId: Id, plannedFor: z.iso.date() }),
  'visit.cancelled': z.strictObject({ plannedInterventionId: Id }),
  // Vague 7 : bilan hebdomadaire du propriétaire (e-mail sans suivi, L4) ; contenu figé dans
  // `weekly_digests.content` (nombres et montants seulement), relu sous RLS par l'identifiant.
  'digest.weekly': WeeklyDigestRef,
  // 02/10/2026 : le garage renvoie le lien de suivi par e-mail (`portalLinks.sendByEmail`). Un e-mail par
  // événement, avec son propre jeton émis au moment de l'envoi (L4) : jamais de jeton ni d'adresse ici.
  'portal_link.email_requested': z.strictObject({ interventionId: Id }),
  // 08/10/2026 : rappel de la prochaine échéance (date) d'un dossier restitué (module `maintenance_reminders`,
  // tâche quotidienne du worker). Un rappel par dossier et par date d'échéance (`maintenance_reminders`).
  'maintenance.reminder_due': z.strictObject({ maintenanceReminderId: Id, interventionId: Id, dueDate: z.iso.date() }),
} as const;

export type EventType = keyof typeof EVENT_PAYLOADS;
export const EVENT_TYPES = Object.keys(EVENT_PAYLOADS) as EventType[];
export const EventTypeSchema = z.enum(EVENT_TYPES as [EventType, ...EventType[]]);

export type EventPayload<T extends EventType> = z.infer<(typeof EVENT_PAYLOADS)[T]>;

/** Ce que les lots passent à `events.emit`. */
export type DomainEvent = { [T in EventType]: { type: T; payload: EventPayload<T> } }[EventType];

export const EventActor = z.object({ type: ActorType, id: Id.nullable() });

/** Enveloppe stockée dans l'outbox et remise au consommateur. */
export const EventEnvelope = z.object({
  id: Id,
  type: EventTypeSchema,
  version: z.literal(1),
  garageId: Id,
  occurredAt: Timestamp,
  actor: EventActor,
  payload: z.record(z.string(), z.unknown()),
});
export type EventEnvelope = z.infer<typeof EventEnvelope>;

export function parseEventPayload<T extends EventType>(type: T, payload: unknown): EventPayload<T> {
  return EVENT_PAYLOADS[type].parse(payload) as EventPayload<T>;
}
